import { Component, Inject, OnInit, Optional } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MessageBoxParameter } from '@classes/message-box-parameter';
import { Tonnage } from '@classes/tonnage';
import { MessageBox } from '@components/message-box/message-box.component';
import { TranslateService } from '@ngx-translate/core';
import { AutocompleteObjectValidator } from '@shared/validators/autocomplete-object-validator';
import { TonnageService } from 'app/core/services/tonnage.service';
import { UiService } from 'app/core/services/ui.service';
// import { environment } from '@environments/environment';
import { Role } from '@enums/role.enum';
import { AccountService } from 'app/core/services/account.service';
import { EnvConfigurationService } from 'app/core/services/env-configuration.service';

@Component({
  selector: 'app-tonnage',
  templateUrl: './tonnage.component.html',
  styleUrls: ['./tonnage.component.scss'],
  styles: [`
  >>> app-tonnage {
    height: 100%;
    display: flex;
  }
`]
})
export class TonnageComponent implements OnInit {
  environment = this.envConfigurationService.environment;

  tonnageForm: FormGroup;

  Role = Role;

  editMode = false;

  constructor(
    private formBuilder: FormBuilder,
    public tonnageService: TonnageService,
    public uiService: UiService,
    private autocompleteObjectValidator: AutocompleteObjectValidator,
    public dialog: MatDialog,
    public translateService: TranslateService,
    public accountService: AccountService,
    public envConfigurationService: EnvConfigurationService,
    @Optional() public dialogRef: MatDialogRef<TonnageComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: Tonnage
  ) { }

  ngOnInit() {
    this.initFormBuilder(this.data);
  }

  private initFormBuilder(value?: Tonnage) {
    var editMode = value ? (value?.id == null || (value?.billed)) : false;
    const fb = this.formBuilder;
    this.tonnageForm = fb.group({
      id: fb.control(value?.id),
      isError: fb.control(value?.isError),
      site: fb.control({ value: value?.site, disabled: editMode }, [Validators.required, this.autocompleteObjectValidator.autocompleteObjectValidator()]),
      company: fb.control({ value: value?.company, disabled: editMode }, [Validators.required, this.autocompleteObjectValidator.autocompleteObjectValidator()]),
      date: fb.control({ value: value?.date, disabled: editMode }, [Validators.required]),
      type: fb.control({ value: value?.isLoading != null ? (value?.isLoading ? '1' : '0') : null, disabled: editMode }, [Validators.required]),
      quantity: fb.control({ value: value?.quantity, disabled: editMode }, [Validators.required]),
      isExceptionalConvoy: fb.control({ value: value?.isExceptionalConvoy, disabled: editMode }, []),
      shipName: fb.control({ value: value?.shipName, disabled: editMode }, []),
      origineName: fb.control({ value: value?.origineName, disabled: editMode }, []),
      destinationName: fb.control({ value: value?.destinationName, disabled: editMode }, []),
      orderReference: fb.control({ value: value?.orderReference, disabled: editMode }, [Validators.maxLength(150)]),
      material: fb.control({ value: value?.material, disabled: editMode }, [Validators.required, this.autocompleteObjectValidator.autocompleteObjectValidator()]),
      origineSite: fb.control({ value: value?.origineSite, disabled: editMode }, [Validators.required, this.autocompleteObjectValidator.autocompleteObjectValidator()]),
      destinationSite: fb.control({ value: value?.destinationSite, disabled: editMode }, [Validators.required, this.autocompleteObjectValidator.autocompleteObjectValidator()]),
      ship: fb.control({ value: value?.ship, disabled: editMode }, [Validators.required, this.autocompleteObjectValidator.autocompleteObjectValidator()]),
    });
    this.initUserData();
  }

  private initUserData() {
    if (this.accountService.currentUser.value?.company) {
      this.tonnageForm.get("company")?.setValue(this.accountService.currentUser.value?.company);
      this.tonnageForm.get("company")?.disable();
    }
  }

  onSubmit() {

    var checkBeforeSubmit = () => {
      if (this.data?.id && this.data?.isError && this.environment.application == 'supervision' && !this.data?.billed) {
        const messageBoxParameter = new MessageBoxParameter();

        messageBoxParameter.message = this.translateService.instant('label.remove-error-status');
        messageBoxParameter.dialog = this.dialog;
        messageBoxParameter.title = 'Attention';
        messageBoxParameter.buttons = ['yes', 'no'];
        messageBoxParameter.width = "400px";

        MessageBox.show(messageBoxParameter).subscribe(mbResult => {
          if (mbResult && mbResult.value) {
            this.tonnageForm.controls.isError.setValue(false);
            this.submit();
          }
          else {
            this.submit();
          }
        })
      } else {
        this.submit();
      }
    }

    if (this.tonnageForm.get("quantity")?.value > 2000) {
      const messageBoxParameter = new MessageBoxParameter();

      messageBoxParameter.message = this.translateService.instant('tonnage.quantity-exeed');
      messageBoxParameter.dialog = this.dialog;
      messageBoxParameter.title = 'Attention';
      messageBoxParameter.buttons = ['yes', 'no'];
      messageBoxParameter.width = "400px";

      MessageBox.show(messageBoxParameter).subscribe(mbResult => {
        if (mbResult && mbResult.value) {
          checkBeforeSubmit();
        }
        else {
          return false;
        }
      })
    }
    else {
      checkBeforeSubmit();
    }

  }

  private submit() {
    this.uiService.addRouteLoading();
    const tonnage = new Tonnage(
      this.tonnageForm.value
    );
    tonnage.isLoading = this.tonnageForm.value.type == "1";
    tonnage.origineName = this.tonnageForm.get("origineSite")?.value.name;
    tonnage.destinationName = this.tonnageForm.get("destinationSite")?.value.name;
    tonnage.shipName = this.tonnageForm.get("ship")?.value.name;
    tonnage.company = this.tonnageForm.get("company")?.value;
    tonnage.companyId = this.tonnageForm.get("company")?.value.id;
    this.tonnageService.createOrUpdateTonnage(tonnage).subscribe(data => {
      this.initFormBuilder();
      this.uiService.removeRouteLoading();
      this.uiService.displayMessageToast('tonnage.success-edit');
      if (this.dialogRef) {
        this.dialogRef.close(true);
      }
    }, error => {
      console.error(error);
      this.uiService.removeRouteLoading();
    })
  }

  patch() {
    this.uiService.addRouteLoading();
    this.tonnageService.createOrUpdateTonnage({
      id: this.tonnageForm.get("id")?.value,
      quantity: this.tonnageForm.get("quantity")?.value,
      shipName: this.tonnageForm.get("ship")?.value.name,
      materialId: this.tonnageForm.get("material")?.value.id,
    }).subscribe(data => {
      this.initFormBuilder();
      this.uiService.removeRouteLoading();
      this.uiService.displayMessageToast('tonnage.success-edit');
      if (this.dialogRef) {
        this.dialogRef.close(true);
      }
    }, error => {
      console.error(error);
      this.uiService.removeRouteLoading();
    })
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  onDelete() {
    if (this.data && this.data.id) {

      const messageBoxParameter = new MessageBoxParameter();

      messageBoxParameter.message = this.translateService.instant('tonnage.confirm-delete');
      messageBoxParameter.dialog = this.dialog;
      messageBoxParameter.title = 'Confirmation';
      messageBoxParameter.buttons = ['yes', 'no'];
      messageBoxParameter.width = "400px";

      MessageBox.show(messageBoxParameter).subscribe(mbResult => {
        if (mbResult && mbResult.value) {
          this.uiService.addRouteLoading();
          this.tonnageService.deleteTonnage(this.data?.id || "").subscribe(data => {
            this.uiService.displayMessageToast('tonnage.success-delete');
            this.uiService.removeRouteLoading();
            if (this.dialogRef) {
              this.dialogRef.close(true);
            }
          }, error => {
            console.error(error);
            this.uiService.removeRouteLoading();
          });
        }
      });
    }
  }

  onEdit() {
    const messageBoxParameter = new MessageBoxParameter();

    messageBoxParameter.message = this.translateService.instant('tonnage.confirm-edit');
    messageBoxParameter.dialog = this.dialog;
    messageBoxParameter.title = 'Confirmation';
    messageBoxParameter.buttons = ['yes', 'no'];
    messageBoxParameter.width = "600px";

    MessageBox.show(messageBoxParameter).subscribe(mbResult => {
      if (mbResult && mbResult.value) {
        this.tonnageForm.controls.quantity.enable();
        this.tonnageForm.controls.material.enable();
        this.tonnageForm.controls.ship.enable();
        this.editMode = true;
      }
    });
  }

  isRequiredField(field: string) {
    const form_field = <AbstractControl>this.tonnageForm.get(field);
    if (!form_field.validator) {
      return false;
    }

    const validator = form_field.validator({} as AbstractControl);
    return (validator && validator.required);
  }
}
